import React, { useState } from "react";
import { Mail, Lock, Eye } from "lucide-react";
import { resolveAsset } from "@/components/kit/FileUpload";
import { cn } from "@/lib/utils";

const ACCENT = "#22C55E";

function MockField({ icon: Icon, label, required }) {
  return (
    <div className="space-y-1">
      <label className="text-[10px] font-medium text-white/60">
        {label}{required && <span className="ml-0.5 text-[#EF4444]">*</span>}
      </label>
      <div className="flex h-9 items-center gap-2 rounded-md border border-white/[0.12] bg-white/[0.03] px-2.5">
        {Icon && <Icon className="h-3.5 w-3.5 text-white/40" />}
        <div className="h-2 w-1/2 rounded bg-white/10" />
      </div>
    </div>
  );
}

const iconFor = (key) => (key === "email" ? Mail : key === "password" ? Lock : null);

export function RegistrationPreview({ networkName, logo, sideImage, fields = [] }) {
  const [mode, setMode] = useState("login");
  const img = resolveAsset(sideImage);
  const lg = resolveAsset(logo);
  const name = networkName || "Your Network";

  // Visible fields only; register mode shows all enabled fields, login shows email+password.
  const visible = (fields || []).filter((f) => f.enabled !== false);
  const registerFields = visible.length
    ? visible
    : [{ key: "email", label: "Email", required: true }, { key: "password", label: "Password", required: true }];
  const loginFields = [
    { key: "email", label: "Email address", required: true },
    { key: "password", label: "Password", required: true },
  ];
  const shown = mode === "login" ? loginFields : registerFields;

  return (
    <div className="space-y-3" data-testid="registration-preview">
      <div className="flex items-center justify-between">
        <span className="flex items-center gap-1.5 text-xs text-[#22C55E]"><span className="h-1.5 w-1.5 rounded-full bg-[#22C55E]" /> Live preview</span>
        <div className="flex rounded-lg border border-white/[0.1] p-0.5">
          {["login", "register"].map((m) => (
            <button key={m} onClick={() => setMode(m)} data-testid={`preview-tab-${m}`}
              className={cn("rounded-md px-3 py-1 text-xs capitalize transition-colors", mode === m ? "bg-white text-black" : "text-muted-foreground hover:text-foreground")}>
              {m}
            </button>
          ))}
        </div>
      </div>

      <div className="overflow-hidden rounded-xl border border-white/[0.1] bg-[#0E0E11]">
        <div className="flex h-11 items-center justify-center border-b border-white/[0.06] px-4">
          {lg ? <img src={lg} alt="" className="h-6 w-auto object-contain" /> : <span className="text-sm font-semibold tracking-wide text-white">{name}</span>}
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2">
          <div className="relative hidden min-h-[320px] sm:block">
            {img
              ? <img src={img} alt="" className="absolute inset-0 h-full w-full object-cover" />
              : (
                <div className="absolute inset-0 flex flex-col items-center justify-center gap-3 bg-gradient-to-br from-[#141417] via-[#1a1a1e] to-[#0A0A0A] p-6 text-center">
                  <div className="text-2xl font-bold text-white/90">{name}</div>
                  <div className="h-px w-16 bg-white/20" />
                  <p className="text-xs text-white/40">Auto-generated panel — upload an image to replace it.</p>
                </div>
              )}
          </div>
          <div className="space-y-3.5 p-6">
            <div>
              <h3 className="text-lg font-semibold text-white">{mode === "login" ? "Sign in" : "Create account"}</h3>
              <p className="text-xs text-white/50">{mode === "login" ? "Log in to your partner account" : "Join the affiliate network"}</p>
            </div>
            {shown.map((f) => <MockField key={f.key} icon={iconFor(f.key)} label={f.label} required={f.required} />)}
            <div className="flex h-9 items-center justify-center rounded-md text-xs font-semibold text-black" style={{ background: ACCENT }}>
              {mode === "login" ? "Sign in" : "Create account"}
            </div>
            <div className="flex items-center justify-between text-[10px] text-white/40">
              <span>{mode === "login" ? "Create new account" : "Already have an account?"}</span>
              <span className="inline-flex items-center gap-1"><Eye className="h-3 w-3" /> {mode === "login" ? "Forgot password" : "Log in"}</span>
            </div>
          </div>
        </div>
      </div>
      <p className="text-xs text-muted-foreground/70">Exactly what your media-buyers see. Logo, side image, network name and the registration fields all come from your settings.</p>
    </div>
  );
}
